import {
  formatCurrency,
  formatDate,
  getEFTTypeDisplayName,
  filterCommissionTrustActivityEFTs,
  getPaymentPlanDisplayName,
  isReversalLedgerEftRef,
  resolveCommissionTrustDisplayEftNumber,
  formatCommissionTrustActivityEftNumber,
} from "./eftUtils";
import { DEFAULT_TAX_RATE, INCLUSIVE_HST_FRACTION } from "../constants/tax";
import {
  formatPercentDisplayForSheet,
  roundTo2,
  roundTo2Str,
} from "./numberUtils";
import {
  getBuyerRebateListingSellingForSheet,
  isBuyerRebateIncludedYes,
} from "./tradeFinancialUtils";
import {
  officeExpensesBaseForAgent,
  officeExpensesTaxForAgent,
  officeExpensesTotalForAgent,
  buildOfficeExpenseFinancialRows,
  addOfficeExpensesToNetToOfficeValues,
  getAgentFinancialTotalsBeforeOfficeExp,
} from "./officeExpenses";

function get(obj, path, fallback = "-") {
  return path
    .split(".")
    .reduce((o, k) => (o && o[k] !== undefined ? o[k] : fallback), obj);
}

/**
 * Same HTML as Trade Details → Print (full trade record sheet).
 * Used after finalize to archive PDF on the server (no Dropbox).
 */
export async function buildTradeRecordSheetPrintHtml(
  trade,
  axiosInstance,
  selectedAgentIndex = null,
) {
  const currentDate = new Date().toLocaleDateString();
      // Fetch EFTs for print preview
      let realEstateTrustEFTs = [];
      let commissionTrustEFTs = [];
      let eftReversals = [];
      let commissionRecalls = [];
      let ledgerEntries = [];
      let agentYTDEfts = []; // All agent commission EFTs for YTD calculation
      let agentInfoForYTD = null; // Agent info for fee calculation
      if (trade && trade._id) {
        try {
          const [realEstateRes, commissionRes, reversalsRes, recallsRes, ledgerRes] =
            await Promise.all([
            axiosInstance.get(`/real-estate-trust-eft/trade/${trade._id}`),
            axiosInstance.get(`/commission-trust-eft/trade/${trade._id}`),
            axiosInstance.get(`/eft-reversals`, { params: { tradeId: trade._id } }),
            axiosInstance.get(`/commission-recalls`, {
              params: { tradeId: trade._id },
            }),
            axiosInstance.get("/ledger"),
          ]);
          realEstateTrustEFTs = realEstateRes.data || [];
          commissionTrustEFTs = commissionRes.data || [];
          eftReversals = reversalsRes.data || [];
          commissionRecalls = recallsRes.data || [];
          // Fetch all commission trust EFTs for YTD calculation (not just for this trade)
          const agentId = trade.agentCommissionList?.[0]?.agentId;
          const agentName = trade.agentCommissionList?.[0]?.agentName;
          if (agentId || agentName) {
            try {
              // Fetch with populated trade data, agent info, and all trades
              const [allEFTsRes, agentsRes, tradesRes] = await Promise.all([
                axiosInstance.get("/commission-trust-eft"),
                axiosInstance.get("/agents"),
                axiosInstance.get("/trades"),
              ]);
              const allCommissionEFTs = allEFTsRes.data || [];
              const agents = agentsRes.data || [];
              const allTrades = tradesRes.data || [];
              // Find agent info for fee calculation
              agentInfoForYTD = agents.find(
                (a) =>
                  a.employeeNo?.toString() === agentId || a._id === agentId,
              );
              // Get current trade numbe
              const currentTradeNumber = parseInt(
                trade.tradeNumber || trade.keyInfo?.tradeNumber || 0,
              );
              // Get the year from the current trade's finalized date for YTD calculation
              // YTD runs from Jan 1 to Dec 31 of the trade's finalized year, resetting on Jan 1st
              const currentTradeFinalizedDate = trade.keyInfo?.finalizedDate
                ? new Date(trade.keyInfo.finalizedDate)
                : null;
              const currentYear = currentTradeFinalizedDate
                ? currentTradeFinalizedDate.getFullYear()
                : new Date().getFullYear();
              // Filter: include all finalized trades for this agent with trade number <= current trade number
              agentYTDEfts = allCommissionEFTs
                .filter((eft) => {
                  if (eft.type !== "AgentCommissionTransfer") return false;
                  const matchesAgent =
                    eft.agentId === agentId || eft.agentName === agentName;
                  if (!matchesAgent) return false;
                  // Find the trade for this EFT
                  const eftTradeId = eft.tradeId?._id || eft.tradeId;
                  const eftTrade = allTrades.find((t) => t._id === eftTradeId);
                  if (!eftTrade || !eftTrade.isFinalized) return false;
                  // Get trade number and check if it's <= current trade number
                  const eftTradeNumber = parseInt(
                    eftTrade.tradeNumber || eftTrade.keyInfo?.tradeNumber || 0,
                  );
                  // Check if finalized in the same year as the current trade
                  const eftFinalizedDate = eftTrade.keyInfo?.finalizedDate
                    ? new Date(eftTrade.keyInfo.finalizedDate)
                    : null;
                  const isInSameYear =
                    eftFinalizedDate &&
                    eftFinalizedDate.getFullYear() === currentYear;
                  return isInSameYear && eftTradeNumber <= currentTradeNumber;
                })
                .map((eft) => {
                  // Attach full trade data to each EFT for buyer rebate calculation
                  const tradeData = allTrades.find(
                    (t) => t._id === eft.tradeId || t._id === eft.tradeId?._id,
                  );
                  return { ...eft, tradeData };
                });
            } catch (err) {
              console.error("Error fetching YTD EFTs:", err);
            }
          }
          // Get ledger entries and filter for this trade
          const allLedgerEntries = ledgerRes.data || [];
          const tradeNumber = trade.tradeNumber || trade.keyInfo?.tradeNumber;
          ledgerEntries = allLedgerEntries.filter(
            (entry) =>
              entry.tradeNumber === tradeNumber ||
              (entry.description &&
                entry.description.includes(`Trade #: ${tradeNumber}`)),
          );
        } catch (err) {
          // fallback to empty arrays
        }
      }
      const builderRepaymentEFTs = (commissionTrustEFTs || []).filter(
        (eft) => eft.type === "BuilderRepayment",
      );
      const generalToCommissionEFTs = (commissionTrustEFTs || []).filter(
        (eft) => eft.type === "GeneralToCommissionTransfer",
      );
      const commissionIncome =
        trade.commission?.commissionIncomeRows?.[0] || {};
      const baseOfficeCommission = "";
      // Get the trade classification
      const tradeClassification = trade.keyInfo?.classification || "";
      const isCoOperatingSide = tradeClassification === "CO-OPERATING SIDE";
      const taxRate =
        trade.customTaxRate != null ? trade.customTaxRate : DEFAULT_TAX_RATE;
      // Calculate Sub-Total and HST for Commission row
      const listingAmount =
        commissionIncome.listingAmount && commissionIncome.listingAmount !== ""
          ? parseFloat(commissionIncome.listingAmount)
          : 0;
      const sellingAmount =
        commissionIncome.sellingAmount && commissionIncome.sellingAmount !== ""
          ? parseFloat(commissionIncome.sellingAmount)
          : 0;
      const subTotal = (listingAmount + sellingAmount).toFixed(2);
      // Calculate HST based on the amounts (use trade's set tax rate when applied)
      const listingTax = (listingAmount * taxRate).toFixed(2);
      const sellingTax = (sellingAmount * taxRate).toFixed(2);
      const hst = (parseFloat(listingTax) + parseFloat(sellingTax)).toFixed(2);
      const total = (parseFloat(subTotal) + parseFloat(hst)).toFixed(2);
      // Get first outside broker row (from commission or outsideBrokers)
      let outsideBrokerInfo = null;
      if (trade.commission?.outsideBrokersRows?.length > 0) {
        outsideBrokerInfo = trade.commission.outsideBrokersRows[0];
      } else if (trade.outsideBrokers?.length > 0) {
        outsideBrokerInfo = trade.outsideBrokers[0];
      }
      // Determine side and amounts
      let obListing = "0.00",
        obSelling = "0.00",
        obSubTotal = "0.00",
        obTax = "0.00",
        obTotal = "0.00",
        obName = "";
      if (outsideBrokerInfo) {
        // Get the broker name
        const brokerName = `${
          outsideBrokerInfo.agentName || outsideBrokerInfo.firstName || ""
        } ${outsideBrokerInfo.lastName || ""}`.trim();
        // Get the company name
        const companyName =
          outsideBrokerInfo.company || outsideBrokerInfo.brokerage || "";
        // Combine name and company
        obName =
          brokerName && companyName
            ? `${brokerName} - ${companyName}`
            : brokerName || companyName || "Outside Broker";
        // Get the selling amount from either source
        const brokerSellingAmount = outsideBrokerInfo.sellingAmount || "0.00";
        // First check the broker's end from outsideBrokers array
        const matchingBrokerInOB = trade.outsideBrokers?.find((ob) => {
          const outsideBrokerName = `${ob.firstName} ${ob.lastName}`.trim();
          return (
            outsideBrokerName ===
              (outsideBrokerInfo.agentName || outsideBrokerInfo.firstName) ||
            ob.company === outsideBrokerInfo.company
          );
        });
        // Determine the end type from either the matching broker or current broker info
        let endType = "";
        if (matchingBrokerInOB) {
          if (
            matchingBrokerInOB.end?.toLowerCase().includes("listing") ||
            matchingBrokerInOB.type?.toLowerCase().includes("listing")
          ) {
            endType = "listing";
          } else {
            endType = "selling";
          }
        } else {
          const end = (
            outsideBrokerInfo.end ||
            outsideBrokerInfo.type ||
            ""
          ).toLowerCase();
          endType = end.includes("listing") ? "listing" : "selling";
        }
        // Place amount in appropriate column based on end type
        if (endType === "listing") {
          obListing = brokerSellingAmount;
          obSelling = "0.00";
        } else {
          obListing = "0.00";
          obSelling = brokerSellingAmount;
        }
        obSubTotal = brokerSellingAmount;
        obTax = outsideBrokerInfo.tax || "0.00";
        obTotal = outsideBrokerInfo.total || "0.00";
      }
      // Agent double ender: both sides in-house — show $0 on Outside Broker row; base office uses full commission
      if (tradeClassification === "AGENT DOUBLE ENDER") {
        obListing = "0.00";
        obSelling = "0.00";
        obSubTotal = "0.00";
        obTax = "0.00";
        obTotal = "0.00";
      }
      // Referral agents (from Agent Info) appear under Expenses and reduce Base Office Commission
      const referralForExpenses = (trade.agentCommissionList || []).filter(
        (a) => (a.classification || "").toUpperCase() === "REFERRAL",
      );
      const referralAmountSum = referralForExpenses.reduce(
        (s, a) => s + parseFloat(a.amount || 0),
        0,
      );
      const referralTaxSum = referralForExpenses.reduce(
        (s, a) => s + parseFloat(a.tax || 0),
        0,
      );
      const referralTotalSum = referralForExpenses.reduce(
        (s, a) => s + parseFloat(a.total || 0),
        0,
      );
      const expensesRowsForPrint = [];
      if (outsideBrokerInfo) {
        expensesRowsForPrint.push({
          name: obName,
          listing: obListing,
          selling: obSelling,
          subTotal: obSubTotal,
          tax: obTax,
          total: obTotal,
        });
      }
      referralForExpenses.forEach((a) => {
        expensesRowsForPrint.push({
          name: `${a.agentName || ""} - Referral`,
          listing: "0.00",
          selling: a.amount || "0",
          subTotal: a.amount || "0",
          tax: a.tax || "0",
          total: a.total || "0",
        });
      });
      // Calculate Base Office Commission
      let baseOfficeCommissionListing,
        baseOfficeCommissionSelling,
        baseOfficeCommissionSubTotal,
        baseOfficeCommissionHST,
        baseOfficeCommissionTotal;
      if (isCoOperatingSide) {
        // For Co-operating Side, base office commission = commission (same values) unless there are referral agents (then deduct referral)
        if (referralForExpenses.length === 0) {
          baseOfficeCommissionListing = listingAmount;
          baseOfficeCommissionSelling = sellingAmount;
          baseOfficeCommissionSubTotal = subTotal;
          baseOfficeCommissionHST = hst;
          baseOfficeCommissionTotal = total;
        } else {
          baseOfficeCommissionListing = listingAmount;
          baseOfficeCommissionSelling = (
            parseFloat(sellingAmount) - referralAmountSum
          ).toFixed(2);
          baseOfficeCommissionSubTotal = (
            parseFloat(subTotal) - referralAmountSum
          ).toFixed(2);
          baseOfficeCommissionHST = (parseFloat(hst) - referralTaxSum).toFixed(
            2,
          );
          baseOfficeCommissionTotal = (
            parseFloat(total) - referralTotalSum
          ).toFixed(2);
        }
      } else {
        // For other classifications, base office commission is the net after outside broker + referral deductions
        baseOfficeCommissionListing = (
          parseFloat(listingAmount) - parseFloat(obListing)
        ).toFixed(2);
        baseOfficeCommissionSelling = (
          parseFloat(sellingAmount) -
          parseFloat(obSelling) -
          referralAmountSum
        ).toFixed(2);
        baseOfficeCommissionSubTotal = (
          parseFloat(subTotal) -
          parseFloat(obSubTotal) -
          referralAmountSum
        ).toFixed(2);
        baseOfficeCommissionHST = (
          parseFloat(hst) -
          parseFloat(obTax) -
          referralTaxSum
        ).toFixed(2);
        baseOfficeCommissionTotal = (
          parseFloat(total) -
          parseFloat(obTotal) -
          referralTotalSum
        ).toFixed(2);
      }
      // Calculate Agent Commission for all agents
      // For AGENT DOUBLE ENDER, track transaction order per agent
      const agentDoubleEnderOrder = new Map();
      if (tradeClassification === "AGENT DOUBLE ENDER") {
        (trade.agentCommissionList || []).forEach((a, index) => {
          if (a.classification === "AGENT DOUBLE ENDER" && a.agentId) {
            const key = a.agentId;
            if (!agentDoubleEnderOrder.has(key)) {
              agentDoubleEnderOrder.set(key, []);
            }
            agentDoubleEnderOrder.get(key).push(index);
          }
        });
      }
      const agentCalculations = (trade.agentCommissionList || []).map(
        (agent, agentIndex) => {
          const agentAmount =
            agent.amount && agent.amount !== "" ? parseFloat(agent.amount) : 0;
          const agentFeesDeducted =
            agent.feesDeducted && agent.feesDeducted !== ""
              ? parseFloat(agent.feesDeducted)
              : 0;
          const agentClassification = agent.classification?.toLowerCase() || "";
          const buyerRebateAmount =
            isBuyerRebateIncludedYes(agent) && agent.buyerRebateAmount
              ? parseFloat(agent.buyerRebateAmount) || 0
              : 0;
          const shareAfterPlan = agentAmount - agentFeesDeducted;
          const displaySubForSheet = Math.max(
            0,
            shareAfterPlan - buyerRebateAmount,
          );
          // Determine agent amounts based on trade classification (not agent classification)
          let agentListing = "0.00";
          let agentSelling = "0.00";
          // For AGENT DOUBLE ENDER: use transactionType to determine which column
          if (
            tradeClassification === "AGENT DOUBLE ENDER" &&
            agent.classification === "AGENT DOUBLE ENDER"
          ) {
            // Determine transaction type - use saved value or infer from order
            let transactionType = agent.transactionType;
            // If transactionType is missing, infer from order
            if (!transactionType && agent.agentId) {
              const agentIndices =
                agentDoubleEnderOrder.get(agent.agentId) || [];
              const positionInList = agentIndices.indexOf(agentIndex);
              // First occurrence (position 0) = LISTING, second (position 1) = SELLING
              transactionType = positionInList === 0 ? "LISTING" : "SELLING";
            }
            if (transactionType === "LISTING") {
              // First transaction goes to Listing column
              agentListing = displaySubForSheet.toFixed(2);
              agentSelling = "0.00";
            } else if (transactionType === "SELLING") {
              // Second transaction goes to Selling column
              agentListing = "0.00";
              agentSelling = displaySubForSheet.toFixed(2);
            }
          } else if (isCoOperatingSide) {
            // For CO-OPERATING SIDE: put agent amount in selling column
            agentListing = "0.00";
            agentSelling = displaySubForSheet.toFixed(2);
          } else {
            // For LISTING SIDE: put agent amount in listing column
            agentListing = displaySubForSheet.toFixed(2);
            agentSelling = "0.00";
          }
          // Sub-Total column; Total / HST match Agent Info (net = amount + tax − totalFees).
          let agentSubTotal = (
            parseFloat(agentListing) + parseFloat(agentSelling)
          ).toFixed(2);
          const { agentTotal, agentHST } = getAgentFinancialTotalsBeforeOfficeExp(
            agent,
            parseFloat(agentSubTotal),
            taxRate,
            buyerRebateAmount,
          );
          const officeExpenseFinancialRows = buildOfficeExpenseFinancialRows(
            agent,
            tradeClassification,
            taxRate,
          );
          return {
            ...agent,
            agentListing,
            agentSelling,
            agentSubTotal,
            agentHST: agentHST.toFixed(2),
            agentTotal: agentTotal.toFixed(2),
            officeExpenseFinancialRows,
          };
        },
      );
      const showBaseOfficeAndNetToOffice =
        selectedAgentIndex === null || selectedAgentIndex === undefined;
      // Modal passes index into non-referral agents only; match by that list (by name/identity), not by order in full list.
      const nonReferralAgents = agentCalculations.filter(
        (a) => (a.classification || "").toUpperCase() !== "REFERRAL",
      );
      let agentCalculationsFiltered = agentCalculations;
      if (!showBaseOfficeAndNetToOffice && selectedAgentIndex != null) {
        const indices = Array.isArray(selectedAgentIndex)
          ? selectedAgentIndex
          : [selectedAgentIndex];
        const selected = indices
          .filter((i) => Number.isInteger(i) && nonReferralAgents[i])
          .map((i) => nonReferralAgents[i]);
        if (selected.length > 0) {
          agentCalculationsFiltered = selected;
        }
      }
      // Exclude referral agents from Net to Office agent sum (referral already deducted in Base Office Commission).
      // Net to Office: sub-total + HST on net sub-total (sheet Total excludes rebate add-back; EFT adds rebates separately).
      const agentsForNetToOffice = agentCalculationsFiltered.filter(
        (a) => (a.classification || "").toUpperCase() !== "REFERRAL",
      );
      const agentKeysForBuyerRebateSheet = showBaseOfficeAndNetToOffice
        ? null
        : new Set(
            agentsForNetToOffice.map((a) =>
              String(a.agentId ?? a.agentName ?? ""),
            ),
          );
      const buyerRebateCols = getBuyerRebateListingSellingForSheet(trade, {
        agentKeys: agentKeysForBuyerRebateSheet,
      });
      const buyerRebateListing = buyerRebateCols.listing.toFixed(2);
      const buyerRebateSelling = buyerRebateCols.selling.toFixed(2);
      const totalBuyerRebate = parseFloat(
        (buyerRebateCols.listing + buyerRebateCols.selling).toFixed(2),
      );
      let netToOfficeListing,
        netToOfficeSelling,
        netToOfficeSubTotal,
        netToOfficeHST,
        netToOfficeTotal;
      let totalAgentListing,
        totalAgentSelling,
        totalAgentSubTotal,
        totalAgentHST,
        totalAgentTotal;
      if (!isCoOperatingSide && agentsForNetToOffice.length === 2) {
        const agent1 = agentsForNetToOffice[0] || {};
        const agent2 = agentsForNetToOffice[1] || {};
        totalAgentListing =
          parseFloat(agent1.agentListing || 0) -
          parseFloat(agent2.agentListing || 0);
        totalAgentSelling =
          parseFloat(agent1.agentSelling || 0) -
          parseFloat(agent2.agentSelling || 0);
        totalAgentSubTotal =
          parseFloat(agent1.agentSubTotal || 0) -
          parseFloat(agent2.agentSubTotal || 0);
        totalAgentHST =
          parseFloat(agent1.agentHST || 0) - parseFloat(agent2.agentHST || 0);
        totalAgentTotal =
          parseFloat(agent1.agentTotal || 0) -
          parseFloat(agent2.agentTotal || 0);
      } else {
        totalAgentListing = agentsForNetToOffice.reduce(
          (sum, agent) => sum + parseFloat(agent.agentListing || 0),
          0,
        );
        totalAgentSelling = agentsForNetToOffice.reduce(
          (sum, agent) => sum + parseFloat(agent.agentSelling || 0),
          0,
        );
        totalAgentSubTotal = agentsForNetToOffice.reduce(
          (sum, agent) => sum + parseFloat(agent.agentSubTotal || 0),
          0,
        );
        totalAgentHST = agentsForNetToOffice.reduce(
          (sum, agent) => sum + parseFloat(agent.agentHST || 0),
          0,
        );
        totalAgentTotal = agentsForNetToOffice.reduce(
          (sum, agent) => sum + parseFloat(agent.agentTotal || 0),
          0,
        );
      }
      if (tradeClassification === "AGENT DOUBLE ENDER") {
        const totalListingToAgents = agentsForNetToOffice.reduce(
          (sum, a) => sum + parseFloat(a.agentListing || 0),
          0,
        );
        const totalSellingToAgents = agentsForNetToOffice.reduce(
          (sum, a) => sum + parseFloat(a.agentSelling || 0),
          0,
        );

        netToOfficeListing = (
          parseFloat(baseOfficeCommissionListing) -
          totalListingToAgents -
          buyerRebateCols.listing
        ).toFixed(2);

        netToOfficeSelling = (
          parseFloat(baseOfficeCommissionSelling) -
          totalSellingToAgents -
          buyerRebateCols.selling
        ).toFixed(2);

        netToOfficeSubTotal = (
          parseFloat(netToOfficeListing) + parseFloat(netToOfficeSelling)
        ).toFixed(2);
        netToOfficeHST = (parseFloat(netToOfficeSubTotal) * taxRate).toFixed(2);
        netToOfficeTotal = (
          parseFloat(netToOfficeSubTotal) + parseFloat(netToOfficeHST)
        ).toFixed(2);
      } else if (!isCoOperatingSide && agentsForNetToOffice.length === 2) {
        // Listing-side two-agent plan: sub-total = plan fees + office expenses;
        // HST = tax on plan fees + override HST to office + office expense tax.
        const totalPlanFees = agentsForNetToOffice.reduce(
          (total, agent) =>
            total +
            parseFloat(agent.feesDeducted || 0) +
            officeExpensesBaseForAgent(agent, taxRate),
          0,
        );
        const planFeesHST = agentsForNetToOffice.reduce(
          (total, agent) =>
            total +
            roundTo2((parseFloat(agent.feesDeducted || 0) || 0) * taxRate),
          0,
        );
        const overriddenHSTToOffice = agentsForNetToOffice.reduce(
          (total, agent) => {
            const isTaxOverridden =
              agent.taxOverridden === true || agent.taxOverridden === "true";
            return (
              total +
              (isTaxOverridden ? parseFloat(agent.amount || 0) * taxRate : 0) +
              officeExpensesTaxForAgent(agent, taxRate)
            );
          },
          0,
        );
        netToOfficeSubTotal = totalPlanFees.toFixed(2);
        netToOfficeHST = (planFeesHST + overriddenHSTToOffice).toFixed(2);
        netToOfficeTotal = (totalPlanFees + planFeesHST + overriddenHSTToOffice).toFixed(
          2,
        );
        // For listing side with 2 agents, distribute the amounts appropriately
        // Since this is listing side, put the amounts in listing column
        netToOfficeListing = netToOfficeSubTotal;
        netToOfficeSelling = "0.00";
      } else {
        netToOfficeListing = (
          parseFloat(baseOfficeCommissionListing) -
          totalAgentListing -
          buyerRebateCols.listing
        ).toFixed(2);
        netToOfficeSelling = (
          parseFloat(baseOfficeCommissionSelling) -
          totalAgentSelling -
          buyerRebateCols.selling
        ).toFixed(2);
        netToOfficeSubTotal = (
          parseFloat(netToOfficeListing) + parseFloat(netToOfficeSelling)
        ).toFixed(2);
        netToOfficeHST = (parseFloat(netToOfficeSubTotal) * taxRate).toFixed(2);
        netToOfficeTotal = (
          parseFloat(netToOfficeSubTotal) + parseFloat(netToOfficeHST)
        ).toFixed(2);
      }
      const isTwoAgentPlan =
        !isCoOperatingSide && agentsForNetToOffice.length === 2;
      if (!isTwoAgentPlan) {
        ({
          netToOfficeListing,
          netToOfficeSelling,
          netToOfficeSubTotal,
          netToOfficeHST,
          netToOfficeTotal,
        } = addOfficeExpensesToNetToOfficeValues(
          {
            netToOfficeListing: netToOfficeListing || "0.00",
            netToOfficeSelling: netToOfficeSelling || "0.00",
            netToOfficeSubTotal,
            netToOfficeHST,
            netToOfficeTotal,
          },
          agentsForNetToOffice,
          tradeClassification,
          taxRate,
        ));
      }
      // Format the outside brokers data (include referral agents from Agent Info for trade record sheet print)
      const getEndValueForPrintBroker = (b) => {
        const matchingBroker1 = trade.outsideBrokers?.find((ob) => {
          const outsideBrokerName = `${ob.firstName} ${ob.lastName}`.trim();
          return (
            outsideBrokerName === b.agentName || ob.company === b.brokerage
          );
        });
        if (matchingBroker1) {
          if (
            matchingBroker1.end?.toLowerCase().includes("listing") ||
            matchingBroker1.type?.toLowerCase().includes("listing")
          )
            return "Listing";
        }
        if (
          b.end?.toLowerCase().includes("listing") ||
          b.type?.toLowerCase().includes("listing")
        )
          return "Listing";
        return "Selling";
      };
      const formattedOutsideBrokersBase =
        trade.commission?.outsideBrokersRows?.map((broker) => ({
          agentName: broker.agentName || "-",
          brokerage: broker.brokerage || "-",
          end: getEndValueForPrintBroker(broker),
          percentage: broker.percentage,
          sellingAmount: broker.sellingAmount || "0.00",
          tax: broker.tax || "0.00",
          total: broker.total || "0.00",
        })) || [];
      const referralAgentsForPrint = (trade.agentCommissionList || []).filter(
        (a) => (a.classification || "").toUpperCase() === "REFERRAL",
      );
      const referralCommissionRowsForPrint = referralAgentsForPrint.map(
        (a) => ({
          agentName: a.agentName || "-",
          brokerage: a.agentName || "Referral",
          end: "Selling",
          percentage: a.percentage,
          sellingAmount: a.amount || "0.00",
          tax: a.tax || "0.00",
          total: a.total || "0.00",
        }),
      );
      const formattedOutsideBrokers = [
        ...formattedOutsideBrokersBase,
        ...referralCommissionRowsForPrint,
      ];
      const referralAsBrokersForPrint = referralAgentsForPrint.map((a) => ({
        type: "Referral",
        firstName: "-",
        lastName: "-",
        company: a.agentName || "Referral",
        end: "Selling End",
        primaryPhone: "-",
      }));
      const allOutsideBrokersForPrint = [
        ...(trade.outsideBrokers || []),
        ...referralAsBrokersForPrint,
      ];
      // Agent Details in print excludes referral agents (they appear under Outside Brokers)
      const agentDetailsForPrint = (agentCalculationsFiltered || []).filter(
        (a) => (a.classification || "").toUpperCase() !== "REFERRAL",
      );
      // ADE has LISTING + SELLING rows per agent — signatures need each person once
      const agentsForTrustAgreement = (() => {
        const byKey = new Map();
        for (const agent of agentDetailsForPrint) {
          const id =
            agent.agentId != null && String(agent.agentId).trim() !== ""
              ? String(agent.agentId).trim()
              : "";
          const name = String(agent.agentName || "")
            .trim()
            .toLowerCase();
          const key = id || name;
          if (!key || byKey.has(key)) continue;
          byKey.set(key, agent);
        }
        return [...byKey.values()];
      })();
      // Get address information from keyInfo
      const streetNumber = get(trade, "keyInfo.streetNumber") || "";
      const streetName = get(trade, "keyInfo.streetName") || "";
      const unit = get(trade, "keyInfo.unit") || "";
      const city = get(trade, "keyInfo.city") || "";
      const province = get(trade, "keyInfo.province") || "";
      const postalCode = get(trade, "keyInfo.postalCode") || "";
      // Format address for display
      const addressLine1 =
        streetNumber && streetName ? `${streetNumber} ${streetName}` : "";
      const addressLine2 = unit || "";
      const addressLine3 = city && province ? `${city}, ${province}` : "";
      const addressLine4 = postalCode || "";
      const printContent = `


     <!DOCTYPE html>
<html>
<head>
<title>Trade Record Sheet - ${trade.tradeNumber}</title>
<style>
/* A4 Optimized Styling - 210mm x 297mm */
* { box-sizing: border-box; margin: 0; padding: 0; }

body {
font-family: Arial, sans-serif;
margin: 0;
padding: 15mm 12mm; /* A4 margins: top/bottom 15mm, left/right 12mm */
font-size: 10px;
line-height: 1.4;
width: 210mm; /* A4 width */
background: white;
}

.header {
display: flex;
justify-content: space-between;
align-items: flex-start;
margin-bottom: 15px;
width: 100%;
}

.logo { width: 70px; height: auto; }

.title {
font-size: 16px;
font-weight: bold;
text-align: center;
flex-grow: 1;
padding: 0 10px;
}

.date-address {
text-align: right;
font-size: 9px;
line-height: 1.3;
}

.date { font-weight: bold; margin-bottom: 4px; }

.address { line-height: 1.3; }

.section {
margin-bottom: 12px;
page-break-inside: avoid; /* Prevent sections from breaking across pages */
}

.section.section-allow-break {
page-break-inside: auto; /* Allow Financial and following to flow into gap and next page */
}

.section-title {
font-size: 12px;
font-weight: bold;
margin-bottom: 6px;
border-bottom: 1px solid #333;
padding-bottom: 2px;
}

.property-grid {
display: grid;
grid-template-columns: 1fr 1fr;
gap: 6px;
margin-bottom: 8px;
}

.property-item {
display: flex;
justify-content: space-between;
font-size: 9px;
line-height: 1.3;
}

.property-label { font-weight: bold; margin-right: 5px; }

table {
width: 100%;
border-collapse: collapse;
margin-bottom: 8px;
font-size: 8px;
page-break-inside: auto;
}

tr { page-break-inside: avoid; page-break-after: auto; }

th, td {
border: 1px solid #ccc;
padding: 3px 4px;
text-align: left;
line-height: 1.2;
}

th {
background-color: #f5f5f5;
font-weight: bold;
font-size: 8px;
}

.financial-grid {
display: grid;
grid-template-columns: 1fr 1fr 1fr;
gap: 8px;
margin-bottom: 10px;
}

.financial-item { text-align: center; }

.financial-label {
font-weight: bold;
margin-bottom: 3px;
font-size: 9px;
}

.financial-value { font-size: 12px; font-weight: bold; }

/* Page break helpers */
.page-break { page-break-after: always; }

.no-break { page-break-inside: avoid; }

@media print {
body {
margin: 0;
padding: 15mm 12mm;
}

.no-print { display: none !important; }

button { display: none !important; }
}
</style>
</head>

<body>

<div class="header">

<div style="display: flex; flex-direction: column; align-items: flex-start;">
<img src="/logo3.jpg" alt="Logo" class="logo">

<div style="font-size: 9px; line-height: 1.2; margin-top: 5px;">
ABC Realty Inc., Brokerage<br>
634 Champlain St Dieppe <br>
NB E1A 1P4<br>
Phone: 123 456 7890
</div>

</div>

<div class="title">Trade Record Sheet</div>

<div class="date-address">

<div class="date">${currentDate}</div>

<div class="address">
${addressLine1}<br>
${addressLine2}<br>
${addressLine3}<br>
${addressLine4}
</div>

<div style="font-size: 10px; font-weight: bold; margin-top: 5px;">
Trade Number - ${get(trade, "keyInfo.tradeNumber") || trade.tradeNumber || "-"}
</div>

</div>

</div>

<div class="section">

<div class="section-title">Property</div>

<div class="property-grid">

<div class="property-item">
<span class="property-label">Type:</span>
<span>${get(trade, "keyInfo.propertyType")}</span>



          </div>

<div class="property-item">
  <span class="property-label">Classification:</span>
  <span>${get(trade, "keyInfo.classification")}</span>
</div>
<div class="property-item">
  <span class="property-label">Status:</span>
  <span>${get(trade, "keyInfo.status")}</span>
</div>

<div class="property-item">
  <span class="property-label">MLS Number:</span>
  <span>${get(trade, "keyInfo.mlsNumber")}</span>
</div>

<div class="property-item">
  <span class="property-label">Offer Date:</span>
  <span>${
    get(trade, "keyInfo.offerDate")
      ? formatDate(get(trade, "keyInfo.offerDate"))
      : ""
  }</span>
</div>

<div class="property-item">
  <span class="property-label">Entry Date:</span>
  <span>${
    get(trade, "keyInfo.entryDate")
      ? formatDate(get(trade, "keyInfo.entryDate"))
      : ""
  }</span>
</div>

<div class="property-item">
  <span class="property-label">Firm Date:</span>
  <span>${
    get(trade, "keyInfo.firmDate")
      ? formatDate(get(trade, "keyInfo.firmDate"))
      : ""
  }</span>
</div>

<div class="property-item">
  <span class="property-label">Close Date:</span>
  <span>${
    get(trade, "keyInfo.closeDate")
      ? formatDate(get(trade, "keyInfo.closeDate"))
      : ""
  }</span>
</div>

</div>

</div>

<div class="section">

  <div class="section-title">Conditions</div>

  ${
    trade.conditions && trade.conditions.length > 0
      ? `

  <table>

    <thead>
      <tr>
        <th>Condition</th>
        <th>Due Date</th>
        <th>Condition Met Date</th>
      </tr>
    </thead>

    <tbody>
      ${trade.conditions
        .map(
          (condition) => `

        <tr>
          <td>${condition.conditionText || "-"}</td>
          <td>${
            condition.dueDate ? formatDate(condition.dueDate) : "-"
          }</td>
          <td>${
            condition.conditionMetDate
              ? formatDate(condition.conditionMetDate)
              : "Not met"
          }</td>
        </tr>

      `,
        )
        .join("")}
    </tbody>

  </table>

  `
      : "<p>No conditions available.</p>"
  }

</div>

<div class="section">

  <div class="section-title">Contacts</div>

  ${
    trade.people && trade.people.length > 0
      ? `

  <table>

    <thead>
      <tr>
        <th>Type</th>
        <th>First Name</th>
        <th>Last Name</th>
        <th>Primary Phone</th>
        <th>Secondary Phone</th>
        <th>Email</th>
        <th>Company Name</th>
      </tr>
    </thead>

    <tbody>
      ${trade.people
        .map(
          (person) => `

        <tr>
          <td>${person.type || "-"}</td>
          <td>${person.firstName || "-"}</td>
          <td>${person.lastName || "-"}</td>
          <td>${person.primaryPhone || "-"}</td>
          <td>${person.secondaryPhone || "-"}</td>
          <td>${person.email || "-"}</td>
          <td>${person.companyName || "-"}</td>
        </tr>

      `,
        )
        .join("")}
    </tbody>

  </table>

  `
      : "<p>No contacts available.</p>"
  }

</div>

<div class="section">

  <div class="section-title">Outside Brokers</div>

  ${
    allOutsideBrokersForPrint && allOutsideBrokersForPrint.length > 0
      ? `

  <table>

    <thead>
      <tr>
        <th>Type</th>
        <th>End</th>
        <th>First Name</th>
        <th>Last Name</th>
        <th>Company</th>
        <th>Phone Number</th>
      </tr>
    </thead>

    <tbody>
      ${allOutsideBrokersForPrint
        .map(
          (broker) => `

        <tr>
          <td>${broker.type || "-"}</td>
          <td>${broker.end || "-"}</td>
          <td>${broker.firstName || "-"}</td>
          <td>${broker.lastName || "-"}</td>
          <td>${broker.company || "-"}</td>
          <td>${broker.primaryPhone || "-"}</td>
        </tr>

      `,
        )
        .join("")}
    </tbody>

  </table>

  `
      : "<p>No outside brokers available.</p>"
  }

</div>

<div class="section">

  <div class="section-title">Outside Brokers Commission Details</div>

  ${
    formattedOutsideBrokers.length > 0
      ? `

  <table>

    <thead>
      <tr>
        <th>Agent Name</th>
        <th>Brokerage</th>
        <th>End</th>
        <th>Percentage</th>
        <th>Selling Amount</th>
        <th>Tax</th>
        <th>Total</th>
      </tr>
    </thead>

    <tbody>
      ${formattedOutsideBrokers
        .map(
          (broker) => `

        <tr>
          <td>${broker.agentName}</td>
          <td>${broker.brokerage}</td>
          <td>${broker.end}</td>
          <td>${formatPercentDisplayForSheet(broker.percentage)}</td>
          <td>${formatCurrency(broker.sellingAmount)}</td>
          <td>${formatCurrency(broker.tax)}</td>
          <td>${formatCurrency(broker.total)}</td>
        </tr>

      `,
        )
        .join("")}
    </tbody>

  </table>

  `
      : "<p>No outside brokers commission details available.</p>"
  }

</div>

<div class="section">

  <div class="section-title">Deposit</div>

  ${
    trade.trustRecords && trade.trustRecords.length > 0
      ? `

  <table>

    <thead>
      <tr>
        <th>We Hold</th>
        <th>Received</th>
        <th>Received From</th>
        <th>Held By</th>
        <th>Deposit Date</th>
        <th>Reference</th>
        <th>Amount</th>
        <th>Payment Type</th>
      </tr>
    </thead>

    <tbody>
      ${trade.trustRecords
        .map(
          (record) => `

        <tr>
          <td>${record.weHold || "-"}</td>
          <td>${record.received || "-"}</td>
          <td>${record.receivedFrom || "-"}</td>
          <td>${record.heldBy || "-"}</td>
          <td>${
            record.depositDate || record.date
              ? formatDate(record.depositDate || record.date)
              : "-"
          }</td>
          <td>${record.reference || "-"}</td>
          <td>${formatCurrency(record.amount || 0)}</td>
          <td>${record.paymentType || "-"}</td>
        </tr>

      `,
        )
        .join("")}
    </tbody>

  </table>

  `
      : "<p>No deposit records available.</p>"
  }

</div>

<div class="section section-allow-break">

  <div class="section-title">Financial</div>

  <div style="font-weight:bold; font-size:12px; margin-bottom:4px;">Financial:</div>

  <div style="margin-bottom:4px; font-size:10px;">
    <div>Selling Price: <span style="font-weight:bold;">${formatCurrency(
      commissionIncome.sellPrice || trade.keyInfo?.sellPrice || 0,
    )}</span></div>
  </div>

  <div style="margin-bottom:4px; font-size:10px;">
    <div>Listing Commission: <span style="font-weight:bold;">${parseFloat(
      trade.keyInfo?.listCommission || 0,
    )}%</span></div>
  </div>

  <div style="margin-bottom:4px; font-size:10px;">
    <div>Selling Commission: <span style="font-weight:bold;">${parseFloat(
      trade.keyInfo?.sellCommission || 0,
    )}%</span></div>
  </div>

  <div style="display:flex; font-weight:bold; border-bottom:1px solid #ccc; padding:3px 0; border-top:1px solid #ccc; font-size:9px;">
    <div style="width:25%"></div>
    <div style="width:12%; text-align:center;">Listing</div>
    <div style="width:12%; text-align:center;">Selling</div>
    <div style="width:12%; text-align:center;">Sub-Total</div>
    <div style="width:12%; text-align:center;">HST</div>
    <div style="width:12%; text-align:center;">Total</div>
  </div>

  <div style="font-weight:bold; margin-top:6px; margin-bottom:0; font-size:10px;">Income</div>

  <div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">
    <div style="width:25%">Commission</div>
    <div style="width:12%; text-align:center;">${formatCurrency(
      listingAmount,
    )}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(
      sellingAmount,
    )}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(subTotal)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(hst)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(total)}</div>
  </div>

  ${
    isCoOperatingSide && referralForExpenses.length === 0
      ? showBaseOfficeAndNetToOffice
        ? `
  <div style="display:flex; padding:3px 0; font-size:9px;">
    <div style="width:25%">Base Office Commission</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionListing)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionSelling)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionSubTotal)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionHST)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionTotal)}</div>
  </div>
  `
        : ""
      : isCoOperatingSide && referralForExpenses.length > 0
        ? showBaseOfficeAndNetToOffice
          ? `
  <div style="font-weight:bold; margin-top:6px; font-size:10px;">Expenses</div>
  ${expensesRowsForPrint
    .filter((row) => (row.name || "").includes("Referral"))
    .map(
      (row) => `
  <div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">
    <div style="width:25%">Ref. ${(row.name || "").replace(/\s*-\s*Referral\s*$/i, "")}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.listing)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.selling)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.subTotal)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.tax)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.total)}</div>
  </div>
  `,
    )
    .join("")}
  <div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">
    <div style="width:25%">Base Office Commission</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionListing)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionSelling)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionSubTotal)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionHST)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionTotal)}</div>
  </div>
  `
          : ""
        : `

  <div style="font-weight:bold; margin-top:6px; font-size:10px;">Expenses</div>
  ${expensesRowsForPrint
    .map(
      (row) => `
  <div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">
    <div style="width:25%">${row.name}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.listing)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.selling)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.subTotal)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.tax)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.total)}</div>
  </div>
  `,
    )
    .join("")}
  ${
    showBaseOfficeAndNetToOffice
      ? `
  <div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">
    <div style="width:25%">Base Office Commission</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionListing)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionSelling)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionSubTotal)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionHST)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(baseOfficeCommissionTotal)}</div>
  </div>
  `
      : ""
  }
  `
  }

  <div style="font-weight:bold; margin-top:6px; font-size:10px;">Agents</div>

  ${agentDetailsForPrint
    .map(
      (agent) => `
  <div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">
    <div style="width:25%">(${agent.agentId || ""}) ${
      agent.agentName || ""
    }</div>
    <div style="width:12%; text-align:center;">${formatCurrency(
      agent.agentListing || "0.00",
    )}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(
      agent.agentSelling || "0.00",
    )}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(
      agent.agentSubTotal || "0.00",
    )}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(
      agent.agentHST || "0.00",
    )}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(
      agent.agentTotal || "0.00",
    )}</div>
  </div>
  ${(agent.officeExpenseFinancialRows || [])
    .map(
      (row) => `
  <div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">
    <div style="width:25%; padding-left:12px;">${row.label}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.listing)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.selling)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.subTotal)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.hst)}</div>
    <div style="width:12%; text-align:center;">${formatCurrency(row.total)}</div>
  </div>`,
    )
    .join("")}
  `,
    )
    .join("")}

  <!-- Liabilities Section -->

  ${
    totalBuyerRebate > 0
      ? `

  <div style="font-weight:bold; margin-top:6px; font-size:10px;">Liabilities</div>

  <div style="display:flex; border-bottom:1px solid #ccc; padding:3px 0; font-size:9px;">

    <div style="width:25%">Buyer Rebate</div>

    <div style="width:12%; text-align:center;">${formatCurrency(
      buyerRebateListing,
    )}</div>

    <div style="width:12%; text-align:center;">${formatCurrency(
      buyerRebateSelling,
    )}</div>

<div style="width:12%; text-align:center;">${formatCurrency(
          (
            parseFloat(buyerRebateListing) + parseFloat(buyerRebateSelling)
          ).toFixed(2),
        )}</div>

<div style="width:12%; text-align:center;">${formatCurrency("0.00")}</div>

<div style="width:12%; text-align:center;">${formatCurrency(
          (
            parseFloat(buyerRebateListing) + parseFloat(buyerRebateSelling)
          ).toFixed(2),
        )}</div>

</div>

`
      : ""
  }

${
  showBaseOfficeAndNetToOffice
    ? `
<div style="font-weight:bold; margin-top:6px; font-size:10px;">Net to Office</div>
<div style="display:flex; border-bottom:3px double #000; padding:3px 0; font-size:9px;">
  <div style="width:25%"></div>
  <div style="width:12%; text-align:center;">${formatCurrency(netToOfficeListing)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(netToOfficeSelling)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(netToOfficeSubTotal)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(netToOfficeHST)}</div>
  <div style="width:12%; text-align:center;">${formatCurrency(netToOfficeTotal)}</div>
</div>
</div>
`
    : `
</div>
`
}

${
  tradeClassification !== "AGENT DOUBLE ENDER"
    ? `

<div class="section section-allow-break">

  <div class="section-title">Agent Details</div>

  ${
    agentDetailsForPrint && agentDetailsForPrint.length > 0
      ? `

  <table style="width:100%; border-collapse:collapse; margin-bottom:10px;">

    <thead>
      <tr style="background-color:#f2f2f2;">
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Name</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Number</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Percentage</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Lead</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Payment Plan</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Base</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Plan Amount</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Office Expenses</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Buyer Rebate</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Total</th>
      </tr>
    </thead>

    <tbody>
      ${agentDetailsForPrint
        .map(
          (agent) => `

        <tr>
          <td style="border:1px solid #ddd; padding:4px;">${
            agent.agentName || "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${
            agent.agentId || "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${formatPercentDisplayForSheet(
            agent.percentage,
          )}</td>

          <td style="border:1px solid #ddd; padding:4px;">${
            agent.lead != null && agent.lead !== "" ? agent.lead : "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${
            getPaymentPlanDisplayName(agent.feeInfo) || "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(
            parseFloat(agent.amount || 0),
          )}</td>

          <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(
            agent.feesDeducted || 0,
          )}</td>

          <td style="border:1px solid #ddd; padding:4px;">${
            officeExpensesTotalForAgent(agent, DEFAULT_TAX_RATE) > 0
              ? formatCurrency(
                  officeExpensesTotalForAgent(agent, DEFAULT_TAX_RATE),
                )
              : "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${
            agent.buyerRebateIncluded === "yes" && agent.buyerRebateAmount
              ? formatCurrency(agent.buyerRebateAmount)
              : "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(
            agent.netCommission != null &&
              String(agent.netCommission).trim() !== ""
              ? parseFloat(agent.netCommission)
              : parseFloat(agent.amount || 0) +
                  parseFloat(agent.tax || 0) -
                  parseFloat(
                    agent.totalFees != null && agent.totalFees !== ""
                      ? agent.totalFees
                      : agent.feesDeducted || 0,
                  ) -
                  (agent.buyerRebateIncluded === "yes" &&
                  agent.buyerRebateAmount
                    ? parseFloat(agent.buyerRebateAmount || 0)
                    : 0),
          )}</td>

        </tr>

      `,
        )
        .join("")}
    </tbody>

  </table>

  `
      : "<p>No agent payment details available.</p>"
  }

</div>

`
    : `

<div class="section section-allow-break">

  <div class="section-title">Agent Details</div>

  ${
    agentDetailsForPrint && agentDetailsForPrint.length > 0
      ? `

  <table style="width:100%; border-collapse:collapse; margin-bottom:10px;">

    <thead>
      <tr style="background-color:#f2f2f2;">
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Name</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Number</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Percentage</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Lead</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Payment Plan</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Agent Base</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Plan Amount</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Office Expenses</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Buyer Rebate</th>
        <th style="border:1px solid #ddd; padding:4px; text-align:left;">Total</th>
      </tr>
    </thead>

    <tbody>
      ${agentDetailsForPrint
        .filter((agent) => agent.classification === "AGENT DOUBLE ENDER")
        .map(
          (agent) => `

        <tr>
          <td style="border:1px solid #ddd; padding:4px;">${
            agent.agentName || "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${
            agent.agentId || "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${formatPercentDisplayForSheet(
            agent.percentage,
          )}</td>

          <td style="border:1px solid #ddd; padding:4px;">${
            agent.lead != null && agent.lead !== "" ? agent.lead : "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${
            getPaymentPlanDisplayName(agent.feeInfo) || "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(
            parseFloat(agent.amount || 0),
          )}</td>

          <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(
            agent.feesDeducted || 0,
          )}</td>

          <td style="border:1px solid #ddd; padding:4px;">${
            officeExpensesTotalForAgent(agent, DEFAULT_TAX_RATE) > 0
              ? formatCurrency(
                  officeExpensesTotalForAgent(agent, DEFAULT_TAX_RATE),
                )
              : "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${
            agent.buyerRebateIncluded === "yes" && agent.buyerRebateAmount
              ? formatCurrency(agent.buyerRebateAmount)
              : "-"
          }</td>

          <td style="border:1px solid #ddd; padding:4px;">${formatCurrency(
            agent.netCommission != null &&
              String(agent.netCommission).trim() !== ""
              ? parseFloat(agent.netCommission)
              : parseFloat(agent.amount || 0) +
                  parseFloat(agent.tax || 0) -
                  parseFloat(
                    agent.totalFees != null && agent.totalFees !== ""
                      ? agent.totalFees
                      : agent.feesDeducted || 0,
                  ) -
                  (agent.buyerRebateIncluded === "yes" &&
                  agent.buyerRebateAmount
                    ? parseFloat(agent.buyerRebateAmount || 0)
                    : 0),
          )}</td>

        </tr>

      `,
        )
        .join("")}
    </tbody>

  </table>

  `
      : "<p>No agent payment details available.</p>"
  }

</div>

`
}

<div class="section section-allow-break">

  <div class="section-title">Trust Activity</div>

  ${
    trade.trustRecords && trade.trustRecords.length > 0
      ? `<table><thead><tr>
          <th>Received From</th><th>Deposit Date</th><th>Reference</th><th>Amount</th>
        </tr></thead><tbody>
        ${trade.trustRecords
          .map(
            (record) => `<tr>
              <td>${record.receivedFrom || "-"}</td>
              <td>${
            record.depositDate || record.date
              ? formatDate(record.depositDate || record.date)
              : "-"
          }</td>
              <td>${record.reference || "-"}</td>
              <td>${formatCurrency(record.amount || 0)}</td>
            </tr>`,
          )
          .join("")}
        </tbody></table>`
      : `<div class="text-gray-500">No trust records available.</div>`
  }

</div>

<div class="section">

  <div class="section-title">Real Estate Trust Payments</div>

  ${
    realEstateTrustEFTs.length > 0
      ? `<table><thead><tr>
          <th>EFT #</th><th>Date</th><th>Amount</th><th>Recipient</th><th>Type</th><th>Description</th>
        </tr></thead><tbody>
        ${realEstateTrustEFTs
          .map(
            (eft) => `<tr>
              <td>${eft.eftNumber || ""}</td>
              <td>${
                eft.chequeDate || eft.date
                  ? formatDate(eft.chequeDate || eft.date)
                  : "-"
              }</td>
              <td>${formatCurrency(eft.amount || "")}</td>
              <td>${eft.recipient || ""}</td>
              <td>${getEFTTypeDisplayName(eft.type) || ""}</td>
              <td>${eft.description || ""}</td>
            </tr>`,
          )
          .join("")}
        </tbody></table>`
      : `<div class="text-gray-500">No Real Estate Trust Payments found for this trade.</div>`
  }

</div>

<div class="section">

  <div class="section-title">Commission Trust Activity</div>

          ${(() => {
            // For Listing Side, include CommissionTransfer from Real Estate Trust Payments as first transaction

            // For Co-Operating Side, include CommissionTransfer with "Commission Received" description as first transaction

            const commissionTrustEFTsForActivity =
              filterCommissionTrustActivityEFTs(commissionTrustEFTs);

            let displayCommissionTrustEFTs = [
              ...commissionTrustEFTsForActivity,
            ];

            // Normalize classification for comparison (case-insensitive)

            const normalizedClassification = (tradeClassification || "")

              .toUpperCase()

              .trim();

            if (
              normalizedClassification === "LISTING SIDE" ||
              normalizedClassification === "AGENT DOUBLE ENDER"
            ) {
              const commissionTransferEFT = realEstateTrustEFTs.find(
                (eft) => eft.type === "CommissionTransfer",
              );

              console.log(
                "Commission Transfer EFT found:",

                commissionTransferEFT,

                "for classification:",

                normalizedClassification,

                "realEstateTrustEFTs:",

                realEstateTrustEFTs,
              );

              if (commissionTransferEFT) {
                displayCommissionTrustEFTs = [
                  commissionTransferEFT,

                  ...commissionTrustEFTsForActivity,
                ];
              }

              // Check if trust amount is less than total commission and add "Received From" transaction as second item

              const trustRecords = trade.trustRecords || [];

              const commissionIncome =
                trade.commission?.commissionIncomeRows?.[0] || {};

              const listingAmount = commissionIncome.listingAmount
                ? parseFloat(commissionIncome.listingAmount)
                : 0;

              const sellingAmount = commissionIncome.sellingAmount
                ? parseFloat(commissionIncome.sellingAmount)
                : 0;

              const taxRateForTrust =
                trade.customTaxRate != null
                  ? trade.customTaxRate
                  : DEFAULT_TAX_RATE;
              const listingTax = listingAmount * taxRateForTrust;

              const sellingTax = sellingAmount * taxRateForTrust;

              const totalCommission =
                listingAmount + sellingAmount + listingTax + sellingTax;

              // Calculate total trust amount

              const trustAmount = trustRecords.reduce((sum, record) => {
                const amount = parseFloat(
                  record.amount || record.depositAmount || record.total || 0,
                );

                return sum + amount;
              }, 0);

              // If trust amount is less than total commission, find and add "Received From" transaction

              if (trustAmount < totalCommission) {
                // First, try to find the "Received From" transaction from ledger entries (account 10004 with "Received From")

                // Exclude the CommissionTransfer entry by checking if it matches the CommissionTransfer EFT number

                let receivedFromLedger = null;

                if (ledgerEntries && ledgerEntries.length > 0) {
                  // Get the CommissionTransfer EFT number to exclude it

                  const commissionTransferEFTNumber =
                    commissionTransferEFT?.eftNumber?.toString() || "";

                  // Find ledger entries that have "Received From:" but exclude the CommissionTransfer one

                  // The balance payment entry has format: "Trade #: X - Received From: Name" or "Trade #: X - Address - Received From: Name" (no "Commission Trust")

                  // The CommissionTransfer entry has format: "Trade #: X, Received From: Name - Commission Trust - Address"

                  // Also check for entries from the "final 2 transactions" section (transaction 13) which uses popupDescription

                  const candidateEntries = ledgerEntries.filter(
                    (entry) =>
                      entry.accountNumber === "10004" &&
                      entry.debit > 0 &&
                      entry.description &&
                      (entry.description.includes("Received From:") ||
                        entry.description.includes("Received from:")) &&
                      // Exclude if it matches the CommissionTransfer EFT number

                      entry.eftNumber?.toString() !==
                        commissionTransferEFTNumber &&
                      // Exclude entries that have "Commission Trust" in description (those are from CommissionTransfer)

                      !entry.description.includes("Commission Trust") &&
                      // Exclude entries that have "Commission Trust" anywhere (to be safe)

                      !entry.description

                        .toLowerCase()

                        .includes("commission trust") &&
                      !isReversalLedgerEftRef(entry.eftNumber),
                  );

                  // If we have candidates, prefer the one that matches the pattern from final transactions

                  // (format: "Trade #: X - Received From: Name" or "Trade #: X - Address - Received From: Name")

                  if (candidateEntries.length > 0) {
                    // Find the one that matches the final transaction pattern (has " - Received From:" with dash before it)

                    receivedFromLedger =
                      candidateEntries.find((entry) =>
                        entry.description.includes(" - Received From:"),
                      ) || candidateEntries[0];
                  }
                }

                if (receivedFromLedger) {
                  // Extract recipient from description (format: "Trade #: X - Received From: Recipient - Commission Trust - Address")

                  let recipient = receivedFromLedger.payee || "";

                  if (!recipient && receivedFromLedger.description) {
                    const receivedFromMatch =
                      receivedFromLedger.description.match(
                        /Received From:\s*([^-]+?)(?:\s*-|$)/i,
                      );

                    if (receivedFromMatch) {
                      recipient = receivedFromMatch[1].trim();
                    }
                  }

                  // Create the balance commission payment transaction from ledger entry

                  const balanceCommissionPayment = {
                    eftNumber: resolveCommissionTrustDisplayEftNumber(
                      receivedFromLedger,
                      {
                        eftReversals,
                        realEstateTrustEFTs,
                        commissionTrustEFTs,
                        ledgerEntries,
                      },
                    ),

                    date:
                      receivedFromLedger.date || receivedFromLedger.chequeDate,

                    chequeDate:
                      receivedFromLedger.chequeDate || receivedFromLedger.date,

                    amount:
                      receivedFromLedger.debit || receivedFromLedger.amount,

                    recipient: recipient || "N/A",

                    type: "BalanceCommissionPayment",

                    description: "Balance Amount Received",

                    _id: `balance-${receivedFromLedger._id || Date.now()}`,
                  };

                  // Insert as second transaction (after CommissionTransfer)

                  if (commissionTransferEFT) {
                    displayCommissionTrustEFTs = [
                      commissionTransferEFT,

                      balanceCommissionPayment,

                      ...commissionTrustEFTsForActivity,
                    ];
                  } else {
                    displayCommissionTrustEFTs = [
                      balanceCommissionPayment,

                      ...commissionTrustEFTsForActivity,
                    ];
                  }
                } else {
                  // Fallback: Find the "Received From" transaction from trust records

                  const receivedFromRecord = trustRecords.find(
                    (record) =>
                      record.receivedFrom && record.paymentType === "EFT",
                  );

                  if (receivedFromRecord) {
                    // Extract EFT number from reference or use a default

                    const eftNumber =
                      receivedFromRecord.reference?.replace("EFT#", "") ||
                      receivedFromRecord.eftNumber ||
                      "";

                    // Create the balance commission payment transaction

                    const balanceCommissionPayment = {
                      eftNumber: eftNumber,

                      date:
                        receivedFromRecord.depositDate ||
                        receivedFromRecord.date,

                      chequeDate:
                        receivedFromRecord.depositDate ||
                        receivedFromRecord.date,

                      amount: parseFloat(
                        receivedFromRecord.amount ||
                          receivedFromRecord.depositAmount ||
                          receivedFromRecord.total ||
                          0,
                      ),

                      recipient: receivedFromRecord.receivedFrom || "",

                      type: "BalanceCommissionPayment",

                      description: "Balance Amount Received",

                      _id: `balance-${receivedFromRecord._id || Date.now()}`,
                    };

                    // Insert as second transaction (after CommissionTransfer)

                    if (commissionTransferEFT) {
                      displayCommissionTrustEFTs = [
                        commissionTransferEFT,

                        balanceCommissionPayment,

                        ...commissionTrustEFTsForActivity,
                      ];
                    } else {
                      displayCommissionTrustEFTs = [
                        balanceCommissionPayment,

                        ...commissionTrustEFTsForActivity,
                      ];
                    }
                  }
                }
              }
            } else if (
              tradeClassification === "CO-OPERATING SIDE" ||
              tradeClassification === "Co-Operating Side"
            ) {
              // First, try to find CommissionTransfer in Real Estate Trust Payments

              let commissionReceivedEFT = realEstateTrustEFTs.find(
                (eft) => eft.type === "CommissionTransfer",
              );

              // If not found, look for commission trust ledger phrasing (legacy Received From / Paid To on 10004 credits)

              if (!commissionReceivedEFT) {
                commissionReceivedEFT = commissionTrustEFTsForActivity.find(
                  (eft) =>
                    eft.description &&
                    (/(?:Received From|Received from|Paid To|Paid to):\s*[\s\S]*Commission Trust/i.test(
                      eft.description,
                    ) ||
                      eft.description.includes("Commission Received")),
                );
              }

              // If still not found, check ledger entries from trade finalize (account 10004 with "Received From")

              if (
                !commissionReceivedEFT &&
                ledgerEntries &&
                ledgerEntries.length > 0
              ) {
                const commissionReceivedLedger = ledgerEntries.find(
                  (entry) =>
                    entry.accountNumber === "10004" &&
                    entry.debit > 0 &&
                    entry.description &&
                    (entry.description.includes("Received From") ||
                      entry.description.includes("Received from")) &&
                    !isReversalLedgerEftRef(entry.eftNumber),
                );

                if (commissionReceivedLedger) {
                  // Extract recipient from description (format: "Trade #: X - Address - Received From: Recipient")

                  let recipient = commissionReceivedLedger.payee || "";

                  if (!recipient && commissionReceivedLedger.description) {
                    const receivedFromMatch =
                      commissionReceivedLedger.description.match(
                        /Received From:\s*([^-]+?)(?:\s*-|$)/i,
                      );

                    if (receivedFromMatch) {
                      recipient = receivedFromMatch[1].trim();
                    }
                  }

                  // Create EFT-like object from ledger entry

                  commissionReceivedEFT = {
                    eftNumber: resolveCommissionTrustDisplayEftNumber(
                      commissionReceivedLedger,
                      {
                        eftReversals,
                        realEstateTrustEFTs,
                        commissionTrustEFTs,
                        ledgerEntries,
                      },
                    ),

                    date:
                      commissionReceivedLedger.date ||
                      commissionReceivedLedger.chequeDate,

                    chequeDate:
                      commissionReceivedLedger.chequeDate ||
                      commissionReceivedLedger.date,

                    amount:
                      commissionReceivedLedger.debit ||
                      commissionReceivedLedger.amount,

                    recipient: recipient || "N/A",

                    type: "CommissionTransfer",

                    description: "Commission Received",

                    _id: commissionReceivedLedger._id,
                  };
                }
              }

              // If still not found, check by recipient name (common outside broker names)

              if (!commissionReceivedEFT) {
                commissionReceivedEFT = commissionTrustEFTsForActivity.find(
                  (eft) =>
                    eft.recipient &&
                    (eft.recipient.includes("ABC") ||
                      eft.recipient.includes("Realty Inc")),
                );
              }

              if (commissionReceivedEFT) {
                // Create a formatted transaction with "Commission Received" as description

                const formattedEFT = {
                  ...commissionReceivedEFT,

                  description: "Commission Received",
                };

                // If the transaction was found in commissionTrustEFTs, remove it from the array first

                const remainingEFTs =
                  commissionReceivedEFT._id &&
                  commissionTrustEFTsForActivity.some(
                    (eft) => eft._id === commissionReceivedEFT._id,
                  )
                    ? commissionTrustEFTsForActivity.filter(
                        (eft) => eft._id !== commissionReceivedEFT._id,
                      )
                    : commissionTrustEFTsForActivity;

                displayCommissionTrustEFTs = [formattedEFT, ...remainingEFTs];
              }
            }

            if (
              !showBaseOfficeAndNetToOffice &&
              agentCalculationsFiltered &&
              agentCalculationsFiltered.length > 0
            ) {
              const selectedAgents = agentCalculationsFiltered;
              displayCommissionTrustEFTs = displayCommissionTrustEFTs.filter(
                (eft) => {
                  if (eft.type !== "AgentCommissionTransfer") return false;
                  return selectedAgents.some((selectedAgent) => {
                    const selectedAgentId =
                      selectedAgent?.agentId != null
                        ? selectedAgent.agentId.toString()
                        : "";
                    const selectedAgentName = (selectedAgent?.agentName || "")
                      .trim()
                      .toLowerCase();

                    const matchId =
                      selectedAgentId &&
                      eft.agentId != null &&
                      eft.agentId.toString() === selectedAgentId;
                    const matchName =
                      selectedAgentName &&
                      eft.agentName &&
                      (eft.agentName || "").trim().toLowerCase() ===
                        selectedAgentName;
                    const matchRecipient =
                      selectedAgentName &&
                      eft.recipient &&
                      (eft.recipient || "")
                        .trim()
                        .toLowerCase()
                        .includes(selectedAgentName);
                    return matchId || matchName || matchRecipient;
                  });
                },
              );
            }

            displayCommissionTrustEFTs = filterCommissionTrustActivityEFTs(
              displayCommissionTrustEFTs,
            );

            return displayCommissionTrustEFTs.length > 0
              ? `
              <table><thead><tr>
<th>EFT #</th><th>Date</th><th>Amount</th><th>Recipient</th><th>Type</th><th>Description</th>
</tr></thead><tbody>
${displayCommissionTrustEFTs
  .map(
    (eft) => `<tr>
      <td>${formatCommissionTrustActivityEftNumber(eft, {
        eftReversals,
        realEstateTrustEFTs,
        commissionTrustEFTs,
        ledgerEntries,
      })}</td>
      <td>${
        eft.chequeDate || eft.date
          ? formatDate(eft.chequeDate || eft.date)
          : "-"
      }</td>
      <td>${formatCurrency(eft.amount || "")}</td>
      <td>${eft.recipient || ""}</td>
      <td>${getEFTTypeDisplayName(eft.type) || ""}</td>
      <td>${eft.description || ""}</td>
    </tr>`,
  )
  .join("")}
</tbody></table>`
              : `<div class="text-gray-500">No Commission Trust Activity found for this trade.</div>`;
          })()}

</div>

<div class="section">

  <div class="section-title">EFT Reversals</div>

  ${
    eftReversals && eftReversals.length > 0
      ? `<table><thead><tr>
          <th>Reversal Date</th><th>Section</th><th>Original EFT #</th><th>Amount</th><th>Description</th>
        </tr></thead><tbody>
        ${eftReversals
          .map((r) => {
            const sectionLabel =
              r.section === "real-estate"
                ? "Real Estate Trust"
                : r.section === "commission"
                  ? "Commission Trust"
                  : r.section === "general"
                    ? "General Account"
                    : r.section === "deposit-received"
                      ? "Commission Received from Builder / Brokerage"
                      : r.section || "";
            const reversalDate = r.reversedAt
              ? formatDate(r.reversedAt)
              : r.chequeDate
                ? formatDate(r.chequeDate)
                : "-";
            const desc = r.description || r.notes || "";
            return `<tr>
              <td>${reversalDate}</td>
              <td>${sectionLabel}</td>
              <td>${r.sourceEftNumber || ""}</td>
              <td>${formatCurrency(r.amount || 0)}</td>
              <td>${desc}</td>
            </tr>`;
          })
          .join("")}
        </tbody></table>`
      : `<div class="text-gray-500">No EFT reversals recorded for this trade.</div>`
  }

</div>

<div class="section">

  <div class="section-title">Commission Recalled</div>

  ${
    commissionRecalls && commissionRecalls.length > 0
      ? `<table><thead><tr>
          <th>Recall Date</th><th>Agent</th><th>Reference</th><th>Amount</th><th>Description</th>
        </tr></thead><tbody>
        ${commissionRecalls
          .map((r) => {
            const recallDate = r.recalledAt
              ? formatDate(r.recalledAt)
              : "-";
            const recallSheetDesc = `Trade #: ${r.tradeNumber || ""}, Commission recalled - ${r.agentName || ""}`;
            return `<tr>
              <td>${recallDate}</td>
              <td>${r.agentName || "-"} (${r.agentId || ""})</td>
              <td>${r.recallRef || ""}</td>
              <td>${formatCurrency(r.amount || 0)}</td>
              <td>${recallSheetDesc}</td>
            </tr>`;
          })
          .join("")}
        </tbody></table>`
      : `<div class="text-gray-500">No commission recalls recorded for this trade.</div>`
  }

</div>

<div class="section">

  <div class="section-title">Builder Repayment (Payment to Builder)</div>

  ${
    builderRepaymentEFTs && builderRepaymentEFTs.length > 0
      ? `<table><thead><tr>
          <th>EFT Date</th><th>Builder</th><th>EFT #</th><th>Amount</th><th>Description</th>
        </tr></thead><tbody>
        ${builderRepaymentEFTs
          .map((eft) => {
            const payDate = eft.chequeDate || eft.date
              ? formatDate(eft.chequeDate || eft.date)
              : "-";
            const sheetDesc =
              eft.description ||
              `Trade #: ${trade.tradeNumber || trade.keyInfo?.tradeNumber || ""}, Builder repayment - ${eft.recipient || ""}`;
            return `<tr>
              <td>${payDate}</td>
              <td>${eft.recipient || "-"}</td>
              <td>${eft.eftNumber || ""}</td>
              <td>${formatCurrency(eft.amount || 0)}</td>
              <td>${sheetDesc}</td>
            </tr>`;
          })
          .join("")}
        </tbody></table>`
      : `<div class="text-gray-500">No builder repayments recorded for this trade.</div>`
  }

</div>

<div class="section">

  <div class="section-title">General to Commission Transfer</div>

  ${
    generalToCommissionEFTs && generalToCommissionEFTs.length > 0
      ? `<table><thead><tr>
          <th>EFT Date</th><th>EFT #</th><th>Amount</th><th>Description</th>
        </tr></thead><tbody>
        ${generalToCommissionEFTs
          .map((eft) => {
            const payDate = eft.chequeDate || eft.date
              ? formatDate(eft.chequeDate || eft.date)
              : "-";
            const sheetDesc =
              eft.description ||
              `Trade #: ${trade.tradeNumber || trade.keyInfo?.tradeNumber || ""}, Commission transfer from General to Commission`;
            return `<tr>
              <td>${payDate}</td>
              <td>${eft.eftNumber || ""}</td>
              <td>${formatCurrency(eft.amount || 0)}</td>
              <td>${sheetDesc}</td>
            </tr>`;
          })
          .join("")}
        </tbody></table>`
      : `<div class="text-gray-500">No general to commission transfers recorded for this trade.</div>`
  }

</div>

<div class="section">

  <div class="section-title">Commission Trust Agreement</div>

  <div style="margin-bottom: 15px; font-size: 10px; line-height: 1.4;">
    It is understood between all parties that this agreement shall constitute a Commission Trust Agreement as set out in the contract.
  </div>

  <div style="margin-bottom: 15px; font-size: 10px; line-height: 1.4;">
    To the best of my knowledge the above information is correct. Dated at Markham on ${currentDate}
  </div>

  ${(agentsForTrustAgreement && agentsForTrustAgreement.length > 0
    ? agentsForTrustAgreement
    : []
  )
    .map(
      (agent) => `
  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
    <div style="font-size: 10px;">
      ${agent.agentName || "Agent Name"}, ${agent.agentId ?? "Agent Number"}
    </div>
    <div style="border-bottom: 1px solid #000; width: 200px; height: 20px;"></div>
  </div>
`,
    )
    .join("")}
  ${
    !agentsForTrustAgreement || agentsForTrustAgreement.length === 0
      ? `
  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
    <div style="font-size: 10px;">Agent Name, Agent Number</div>
    <div style="border-bottom: 1px solid #000; width: 200px; height: 20px;"></div>
  </div>
`
      : ""
  }

  <div style="display: flex; justify-content: space-between; align-items: center;">
    <div style="font-size: 10px;">
      Broker of Record: ABC Realty Inc., Brokerage
    </div>

    <div style="border-bottom: 1px solid #000; width: 200px; height: 20px;"></div>
  </div>

</div>

        ${(() => {
          // Calculate YTD Income Details for the agent - only show if trade is finalized

          return ""; // YTD Income Details removed

          const agentsForYTD =
            agentDetailsForPrint && agentDetailsForPrint.length > 0
              ? agentDetailsForPrint
              : (agentCalculations || []).filter(
                  (a) => (a.classification || "").toUpperCase() !== "REFERRAL",
                );
          if (agentYTDEfts.length > 0 && agentsForYTD.length > 0) {
            const agentId = agentsForYTD[0].agentId;

            const agentName = agentsForYTD[0].agentName;

            // Calculate totals from YTD EFTs

            const totalNetCommission = agentYTDEfts.reduce(
              (sum, eft) => sum + parseFloat(eft.amount || 0),

              0,
            );

            // Calculate fees and buyer rebate from all trades associated with YTD EFTs

            let totalFees = 0;

            let totalBuyerRebate = 0;

            let totalHST = 0;

            // Sum fees, buyer rebates, and HST from each trade's agent commission list

            agentYTDEfts.forEach((eft) => {
              const eftTrade = eft.tradeData || eft.tradeId; // Use attached tradeData or fallback to tradeId

              const netCommission = parseFloat(eft.amount || 0);

              // HST portion of tax-inclusive net commission (same as Agent Summary)
              const hst = netCommission * INCLUSIVE_HST_FRACTION;

              totalHST += hst;

              if (eftTrade && eftTrade.agentCommissionList) {
                // Find agent commission data - try multiple matching strategies

                let tradeAgent = eftTrade.agentCommissionList.find(
                  (ac) => ac.agentId === agentId || ac.agentName === agentName,
                );

                if (!tradeAgent && agentInfoForYTD?.employeeNo) {
                  tradeAgent = eftTrade.agentCommissionList.find(
                    (ac) =>
                      ac.agentId === agentInfoForYTD.employeeNo.toString(),
                  );
                }

                if (!tradeAgent && eftTrade.agentCommissionList.length === 1) {
                  tradeAgent = eftTrade.agentCommissionList[0];
                }

                if (tradeAgent) {
                  // Use totalFees if available, otherwise calculate fees based on agent fee plan

                  let feesDeducted = parseFloat(
                    tradeAgent.totalFees || tradeAgent.feesDeducted || 0,
                  );

                  // If fees not found, calculate based on agent fee plan

                  if (!feesDeducted && agentInfoForYTD?.feeInfo) {
                    const feePlan = agentInfoForYTD.feeInfo;

                    let baseFee = 0;

                    switch (feePlan) {
                      case "plan250":
                        baseFee = 250;

                        break;

                      case "plan500":
                        baseFee = 500;

                        break;

                      case "plan9010":
                        baseFee = netCommission * 0.1;

                        break;

                      case "plan955":
                        baseFee = netCommission * 0.05;

                        break;

                      case "plan8515":
                        baseFee = netCommission * 0.15;

                        break;

                      case "plan5050":
                        baseFee = netCommission * 0.5;

                        break;

                      case "plan8020":
                        baseFee = netCommission * 0.2;

                        break;

                      case "plan150":
                        baseFee = 150;

                        break;

                      default:
                        baseFee = 0;
                    }

                    const taxRateYTD =
                      eftTrade?.customTaxRate != null
                        ? eftTrade.customTaxRate
                        : DEFAULT_TAX_RATE;
                    const taxOnFees = baseFee * taxRateYTD;

                    feesDeducted = baseFee + taxOnFees;
                  }

                  totalFees += feesDeducted;

                  // Get buyer rebate if included

                  if (
                    tradeAgent.buyerRebateIncluded === "yes" &&
                    tradeAgent.buyerRebateAmount
                  ) {
                    totalBuyerRebate += parseFloat(
                      tradeAgent.buyerRebateAmount || 0,
                    );
                  }
                }
              }
            });

            const totalIncludingHST = totalNetCommission;

            const grossEarnings = totalNetCommission + totalFees;

            // NET COMMISSION = TOTAL INCLUDING HST / 113 * 100

            const netCommission = (totalIncludingHST / 113) * 100;

            // HST PAID = NET COMMISSION * 13 / 100

            const hstPaid = netCommission * (13 / 100);

            return `
<div class="section">

<div class="section-title">YTD Income Details</div>

<div style="margin-top: 15px; font-size: 11px; line-height: 1.6;">

<div style="display: flex; justify-content: space-between; margin-bottom: 8px; padding: 5px 0;">
<span style="font-weight: bold;">Gross Earnings(HST Included):</span>
<span>${formatCurrency(grossEarnings)}</span>
</div>

<div style="display: flex; justify-content: space-between; margin-bottom: 8px; padding: 5px 0;">
<span style="font-weight: bold;">Office Share( HST Included):</span>
<span>${formatCurrency(totalFees)}</span>
</div>

<div style="display: flex; justify-content: space-between; margin-bottom: 8px; padding: 5px 0;">
<span style="font-weight: bold;">Net Commission(HST Excluded):</span>
<span>${formatCurrency(netCommission)}</span>
</div>

<div style="display: flex; justify-content: space-between; margin-bottom: 8px; padding: 5px 0;">
<span style="font-weight: bold;">HST Paid To Agent:</span>
<span>${formatCurrency(hstPaid)}</span>
</div>

<div style="display: flex; justify-content: space-between; margin-bottom: 8px; padding: 5px 0;">
<span style="font-weight: bold;">TOTAL INCLUDING HST:</span>
<span>${formatCurrency(totalIncludingHST)}</span>
</div>

<div style="display: flex; justify-content: space-between; margin-bottom: 8px; padding: 5px 0;">
<span style="font-weight: bold;">BUYER REBATE:</span>
<span>${formatCurrency(-totalBuyerRebate)}</span>
</div>

</div>

</div>           
            `;
          }

          return "";
        })()}
      </body>
      </html>
    `;

  return printContent;
}
